"use client";

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { useRef, useState } from "react";
import { AddOn } from "@/lib/types/add-on";
import { Service } from "@/lib/types/service";
import { ErrorBlock } from "../error-block";
import { ServiceCard } from "../service-card";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "../ui/card";
import { AnimatedFlipTabs } from "./flip-tabs";
gsap.registerPlugin(useGSAP, ScrollTrigger, SplitText);
interface PricingSectionProps {
  services: Service[];
  addOns: AddOn[];
  servicesErrorMessage?: string;
}
function PricingSection(t0) {
  const $ = _c(32);
  const {
    services,
    addOns,
    servicesErrorMessage
  } = t0;
  const [currentPricingCard, setCurrentPricingCard] = useState(services[1]._id);
  const [currentPricingCardIndex, setCurrentPricingCardIndex] = useState(1);
  const addOnTypes = [...new Set(addOns.map(_temp))].sort(_temp2);
  const [currentAddOnType, setCurrentAddOnType] = useState(addOnTypes[0]);
  let t1;
  if ($[0] !== currentAddOnType) {
    t1 = addOn_0 => addOn_0.type === currentAddOnType;
    $[0] = currentAddOnType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const filteredAddOns = addOns.filter(t1);
  const containerRef = useRef(null);
  const pinnedRef = useRef(null);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const splitServicesHeading = SplitText.create(".services-heading", {
        type: "chars",
        charsClass: "services-heading-char",
        mask: "chars"
      });
      const splitServicesDesc = SplitText.create(".services-description", {
        type: "words",
        wordsClass: "services-description-word",
        mask: "words"
      });
      const splitAddOnsHeading = SplitText.create(".add-ons-heading", {
        type: "chars",
        charsClass: "add-ons-heading-char",
        mask: "chars"
      });
      const splitAddOnsDesc = SplitText.create(".add-ons-description", {
        type: "words",
        wordsClass: "add-ons-description-word",
        mask: "words"
      });
      gsap.set(".services-tabs", {
        autoAlpha: 0,
        y: 30
      });
      gsap.set(".service-card-item", {
        autoAlpha: 0,
        y: 80
      });
      gsap.set(".addons-tabs", {
        autoAlpha: 0,
        y: 30
      });
      gsap.set(".add-on-cards", {
        autoAlpha: 0
      });
      gsap.set(".add-on-card-item", {
        autoAlpha: 0,
        y: 80
      });
      gsap.set(".addons-phase", {
        autoAlpha: 0
      });
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: "bottom bottom",
          scrub: 0.6,
          pin: pinnedRef.current,
          pinSpacing: false,
          pinType: "transform"
        }
      });
      tl.from(splitServicesHeading.chars, {
        y: "100%",
        ease: "circ.out",
        duration: 0.3,
        stagger: 0.025
      });
      tl.from(splitServicesDesc.words, {
        y: "100%",
        ease: "circ.out",
        duration: 0.25,
        stagger: 0.04
      }, "-=0.2");
      tl.to({}, {
        duration: 0.4
      });
      tl.to(".services-tabs", {
        autoAlpha: 1,
        y: 0,
        ease: "circ.out",
        duration: 0.25
      });
      tl.to(".service-card-item", {
        autoAlpha: 1,
        y: 0,
        ease: "circ.out",
        duration: 0.3,
        stagger: 0.07
      }, "-=0.15");
      tl.to({}, {
        duration: 0.75
      });
      tl.to(".pricing-phase", {
        autoAlpha: 0,
        duration: 0.25
      });
      tl.to(".addons-phase", {
        autoAlpha: 1,
        duration: 0.25
      }, "-=0.1");
      tl.from(splitAddOnsHeading.chars, {
        y: "100%",
        ease: "circ.out",
        duration: 0.3,
        stagger: 0.025
      }, "-=0.15");
      tl.from(splitAddOnsDesc.words, {
        y: "100%",
        ease: "circ.out",
        duration: 0.25,
        stagger: 0.04
      }, "-=0.2");
      tl.to({}, {
        duration: 0.4
      });
      tl.to(".addons-tabs", {
        autoAlpha: 1,
        y: 0,
        ease: "circ.out",
        duration: 0.25
      });
      tl.to(".add-on-cards", {
        autoAlpha: 1,
        duration: 0.2,
        ease: "circ.out"
      });
      tl.to(".add-on-card-item", {
        autoAlpha: 1,
        y: 0,
        ease: "circ.out",
        duration: 0.3,
        stagger: 0.06
      });
      const mm = gsap.matchMedia();
      mm.add("(max-width: 767px)", () => {
        tl.set(".add-on-cards", {
          overflowY: "auto"
        }, "-=0.1");
      });
      tl.to({}, {
        duration: 0.5
      });
      return () => mm.revert();
    };
    t3 = {
      scope: containerRef
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useGSAP(t2, t3);
  const t4 = "h-[450vh] w-full";
  const t5 = "services";
  const t6 = "pinned relative h-screen w-full overflow-hidden";
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="text-center 2xl:gap-3"><h2 className="services-heading text-3xl 2xl:text-7xl font-bold">Pricing</h2><p className="services-description text-xl md:text-2xl 2xl:font-semibold mt-2">A package that will fit your needs</p></div>;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== services) {
    t8 = services.map(_temp3);
    $[5] = services;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = (id, index) => {
      setCurrentPricingCard(id);
      setCurrentPricingCardIndex(index);
    };
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== currentPricingCard || $[9] !== t8) {
    t10 = <div className="services-tabs flex items-center justify-center"><AnimatedFlipTabs items={t8} value={currentPricingCard} onChange={t9} /></div>;
    $[8] = currentPricingCard;
    $[9] = t8;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== currentPricingCardIndex || $[12] !== services || $[13] !== servicesErrorMessage) {
    t11 = <div className="service-cards mx-auto w-fit flex flex-col md:flex-row items-stretch justify-center gap-4 will-change-transform">{servicesErrorMessage ? <div className="service-card-item"><ErrorBlock message={servicesErrorMessage} /></div> : <><div className="service-card-item md:hidden"><ServiceCard service={services[currentPricingCardIndex]} /></div>{services.map(_temp4)}</>}</div>;
    $[11] = currentPricingCardIndex;
    $[12] = services;
    $[13] = servicesErrorMessage;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10 || $[16] !== t11) {
    t12 = <div className="pricing-phase absolute inset-0 flex flex-col items-center justify-start gap-6 px-4 py-20">{t7}{t10}{t11}</div>;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = "addons-phase absolute inset-0 flex flex-col items-center justify-center gap-6 px-1 py-8 md:pt-24 md:pb-10";
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="text-center 2xl:gap-3"><h2 className="add-ons-heading text-3xl 2xl:text-7xl font-bold">Add-Ons</h2><p className="add-ons-description text-xl max-w-xs text-pretty md:text-2xl xl:max-w-lg 2xl:font-semibold mt-2">Features to complement your package</p></div>;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const t15 = addOnTypes.map(_temp6);
  let t16;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = type_0 => setCurrentAddOnType(type_0 as AddOn["type"]);
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] !== currentAddOnType || $[21] !== t15) {
    t17 = <div className="addons-tabs flex items-center justify-center"><AnimatedFlipTabs mobileOnly={false} items={t15} value={currentAddOnType} onChange={t16} /></div>;
    $[20] = currentAddOnType;
    $[21] = t15;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  const t18 = "add-on-cards mx-auto flex w-full max-w-5xl flex-1 flex-wrap max-h-120 items-stretch justify-center gap-4 md:max-h-none lg:max-w-6xl lg:gap-5 xl:gap-4";
  const t19 = filteredAddOns.map(_temp7);
  let t20;
  if ($[23] !== t19) {
    t20 = <div className={t18}>{t19}</div>;
    $[23] = t19;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== t14 || $[26] !== t17 || $[27] !== t20) {
    t21 = <div className={t13}>{t14}{t17}{t20}</div>;
    $[25] = t14;
    $[26] = t17;
    $[27] = t20;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  if ($[29] !== t12 || $[30] !== t21) {
    t22 = <div ref={containerRef} className={t4} id={t5}><div ref={pinnedRef} className={t6}>{t12}{t21}</div></div>;
    $[29] = t12;
    $[30] = t21;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  return t22;
}
function _temp7(addOn_1) {
  return <div key={addOn_1._id} className="add-on-card-item"><Card className="w-85"><CardHeader><CardTitle>{addOn_1.name}</CardTitle><CardDescription>{addOn_1.description}</CardDescription></CardHeader><CardFooter className="h-full text-2xl font-bold flex flex-col items-start justify-end">{addOn_1.price}</CardFooter></Card></div>;
}
function _temp6(type) {
  return {
    id: type,
    label: type.replace(/\b\w/g, _temp5)
  };
}
function _temp5(char) {
  return char.toUpperCase();
}
function _temp4(service_0) {
  return <div key={service_0._id} className="service-card-item hidden md:block overflow-hidden"><ServiceCard service={service_0} /></div>;
}
function _temp3(service) {
  return {
    id: service._id,
    label: service.name
  };
}
function _temp2(left, right) {
  return left.localeCompare(right);
}
function _temp(addOn) {
  return addOn.type;
}
export { PricingSection };